import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Badge, type TBadgeColor } from "@components/Badge/Badge";
import { DotIcon } from "@components/icons";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import {
  POAConsentStatusBadgeColorMap,
  type PoaSpRequestState,
} from "@services/powersOfAttorney/types";
import { POAServiceProviderSource } from "../types";

interface IPowerOfAttorneyHouseAccordionRowProps {
  serviceProviderName: string;
  serviceProviderRequestStateId?: PoaSpRequestState;
  isLocked?: boolean;
  selected?: boolean;
  onCheck?: () => void;
  serviceProviderSource?: POAServiceProviderSource;
}

export const PowerOfAttorneyHouseAccordionRow: React.FC<
  IPowerOfAttorneyHouseAccordionRowProps
> = (props) => {
  const {
    serviceProviderName,
    serviceProviderRequestStateId,
    isLocked,
    selected,
    onCheck,
    serviceProviderSource,
  } = props;
  const { t } = useTranslation();

  const deselectedBadgeVariant = useMemo((): {
    color: TBadgeColor;
    text: string;
  } => {
    if (
      !serviceProviderSource ||
      serviceProviderSource === POAServiceProviderSource.Manual
    ) {
      return { color: "red", text: "notAdded" };
    }

    switch (serviceProviderSource) {
      case POAServiceProviderSource.StatementRecommendation:
        return { color: "blue", text: "yourProvider" };
      case POAServiceProviderSource.GenericRecommendation:
        return { color: "yellow", text: "ourSuggested" };
    }
  }, [serviceProviderSource]);

  return (
    <div className="flex w-full items-center justify-between py-[1.2rem]">
      <span
        className={twMerge(
          "text-base transition-colors",
          !selected && !isLocked && "text-grey-dark",
        )}
      >
        {serviceProviderName}
      </span>
      <div className="flex items-center gap-6">
        {isLocked && serviceProviderRequestStateId && (
          <Badge
            variant="bubble"
            icon={DotIcon}
            color={POAConsentStatusBadgeColorMap[serviceProviderRequestStateId]}
          >
            {t(
              `powerOfAttorney.common.status.${serviceProviderRequestStateId}`,
            )}
          </Badge>
        )}
        {!isLocked && (
          <Badge
            variant="bubble"
            icon={DotIcon}
            color={selected ? "green" : deselectedBadgeVariant.color}
          >
            {selected
              ? t("powerOfAttorney.houseAccordion.badge.added")
              : t(
                  `powerOfAttorney.houseAccordion.badge.${deselectedBadgeVariant.text}`,
                )}
          </Badge>
        )}
        {!isLocked ? (
          <Checkbox
            checked={selected}
            onChange={() => {
              onCheck?.();
            }}
          />
        ) : (
          <div className="size-5"></div>
        )}
      </div>
    </div>
  );
};
